import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, User, AtSign, ArrowRight, CheckCircle } from 'lucide-react';
import { validateEmail, validateUsername } from '../lib/utils';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_0c49a145-2431-460e-8df4-1bea13020ea4/artifacts/j4k88xee_file_00000000c9b871f8a2879288a4f57131.png";

export default function RegisterPage() {
  const { register } = useAuth();
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !username || !password || !confirmPassword) {
      toast.error('Error', { description: 'Please fill in all fields' });
      return;
    }
    
    if (!validateEmail(email)) {
      toast.error('Error', { description: 'Please enter a valid email address' });
      return;
    }
    
    if (!validateUsername(username)) {
      toast.error('Error', { description: 'Username must be 3-20 characters (letters, numbers, underscore)' });
      return;
    }
    
    if (password.length < 6) {
      toast.error('Error', { description: 'Password must be at least 6 characters' });
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Error', { description: 'Passwords do not match' });
      return;
    }
    
    setLoading(true);
    try {
      await register(email, username, password, displayName);
      toast.success('Welcome to FityH!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error('Error', { description: message });
    } finally {
      setLoading(false);
    }
  };

  // Benefits list
  const benefits = [
    "Share photos, videos & go live",
    "AI-powered content creation",
    "Earn from your audience",
    "Join a global community"
  ];

  return (
    <div className="min-h-screen flex bg-[#0a0a0f] text-white overflow-hidden" data-testid="register-page">
      {/* Left Side - Branding (Desktop) */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-500/30 rounded-full blur-[128px] animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-pink-500/30 rounded-full blur-[128px] animate-pulse delay-1000" />
        </div>
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center items-center p-12 w-full">
          <div className="max-w-lg text-center">
            <img src={LOGO_URL} alt="FityH" className="w-24 h-24 mx-auto mb-6 rounded-2xl" />
            <h1 className="text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-pink-500 to-violet-500 bg-clip-text text-transparent">
                Join FityH
              </span>
            </h1>
            <p className="text-2xl font-medium text-white/90 mb-4">
              Your Journey Starts Here
            </p>
            <p className="text-lg text-white/60 mb-8">
              Create, share, and earn with the next generation social platform
            </p>
            
            {/* Benefits */}
            <div className="space-y-3 text-left">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-3 p-3 rounded-xl bg-white/5">
                  <CheckCircle className="w-5 h-5 text-pink-500" />
                  <span className="text-white/80">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Right Side - Register Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12 overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-6">
            <img src={LOGO_URL} alt="FityH" className="w-16 h-16 mx-auto mb-3 rounded-xl" />
            <h1 className="text-3xl font-bold bg-gradient-to-r from-pink-500 to-violet-500 bg-clip-text text-transparent">
              Join FityH
            </h1>
            <p className="text-white/60 mt-1">Create your free account</p>
          </div>
          
          {/* Form Header (Desktop) */}
          <div className="hidden lg:block mb-6">
            <h2 className="text-3xl font-bold mb-2">Create Account</h2>
            <p className="text-white/60">Get started with FityH for free</p>
          </div>
          
          {/* Register Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/80">Email</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-12 h-12 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-pink-500"
                  data-testid="register-email-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-white/80">Username</Label>
                <div className="relative">
                  <AtSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <Input
                    id="username"
                    type="text"
                    placeholder="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value.toLowerCase())}
                    className="pl-12 h-12 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-pink-500"
                    data-testid="register-username-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="displayName" className="text-white/80">Display Name</Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <Input
                    id="displayName"
                    type="text"
                    placeholder="Your Name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="pl-12 h-12 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-pink-500"
                    data-testid="register-displayname-input"
                  />
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/80">Password</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Min. 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-12 pr-12 h-12 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-pink-500"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-white/80">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-12 h-12 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-pink-500"
                  data-testid="register-confirm-password-input"
                />
              </div>
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 rounded-xl font-bold text-lg bg-gradient-to-r from-pink-500 to-violet-500 hover:from-pink-600 hover:to-violet-600 text-white shadow-lg shadow-pink-500/25 transition-all hover:shadow-pink-500/40 hover:scale-[1.02] mt-2"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <span className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Create Account
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </form>
          
          {/* Terms */}
          <p className="text-xs text-white/40 text-center mt-4">
            By signing up, you agree to our{' '}
            <Link to="/rules" className="text-pink-500 hover:underline">Terms</Link> and{' '}
            <Link to="/privacy" className="text-pink-500 hover:underline">Privacy Policy</Link>
          </p>
          
          {/* Divider */}
          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-white/40 text-sm">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>
          
          {/* Login Link */}
          <p className="text-center text-white/60">
            Already have an account?{' '}
            <Link 
              to="/login" 
              className="text-pink-500 font-semibold hover:text-pink-400 transition-colors"
              data-testid="login-link"
            >
              Log in
            </Link>
          </p>
          
          {/* Back to Home */}
          <Link
            to="/"
            className="flex items-center justify-center gap-2 mt-6 text-white/40 hover:text-white/80 transition-colors text-sm"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}
